/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */

/**
 * Helpers autour des motifs prédéfinis d'un statut.
 *
 * Un motif est traduisible : le serveur renvoie `label` déjà traduit dans la
 * langue courante, et `translations` quand elles sont chargées. Selon
 * l'endpoint, les traductions arrivent en tableau (`[{ locale, label }]`) ou en
 * objet (`{ fr: '…' }`) — les deux formes sont normalisées ici.
 */
export function reasonTranslations(reason) {
    if (!reason) return {}

    const translations = reason.translations
    if (!translations) return {}

    if (Array.isArray(translations)) {
        return translations.reduce((acc, translation) => {
            if (translation && translation.locale) {
                acc[translation.locale] = translation.label || translation.name || ''
            }
            return acc
        }, {})
    }

    if (typeof translations === 'object') {
        return Object.keys(translations).reduce((acc, locale) => {
            const value = translations[locale]
            acc[locale] = typeof value === 'string' ? value : value && (value.label || value.name)
            return acc
        }, {})
    }

    return {}
}

/**
 * Libellé à afficher : la traduction de la langue demandée, à défaut celle de
 * la langue de repli, à défaut le `label` déjà traduit par le serveur.
 */
export function reasonLabel(reason, locale, fallbackLocale) {
    if (!reason) return ''

    const translations = reasonTranslations(reason)
    return (
        translations[locale] ||
        translations[fallbackLocale] ||
        reason.label ||
        reason.name ||
        ''
    )
}

/** Motifs proposables à un agent : les actifs, dans l'ordre configuré. */
export function activeReasons(status) {
    if (!status || !Array.isArray(status.reasons)) return []
    return status.reasons
        .filter((reason) => reason.active !== false)
        .sort((a, b) => {
            const order = (a.order || 0) - (b.order || 0)
            return order !== 0 ? order : a.id - b.id
        })
}

/**
 * Faut-il passer par la modale de transition motivée ?
 *
 * Oui quand le statut visé exige un motif — le serveur refuse la transition
 * sans lui (422) — et quand le formulaire porte des contributions citoyennes :
 * un changement de statut y vaut décision publique, l'agent doit pouvoir
 * l'expliquer et annoncer un délai. Un ticket ordinaire vers un statut sans
 * motif obligatoire garde le changement en un clic.
 */
export function requiresTransitionModal(status, form) {
    if (!status) return false
    if (status.requires_reason) return true
    return !!(form && form.contribution)
}
